import React, { ReactNode, useCallback, useMemo } from "react";

import { colors } from "theme/palette";

import ClickAwayListener from "components/ClickAwayListener";
import { TableCell } from "components/Table";
import Text from "components/Text";
import TextField from "components/TextField";

interface ICell {
  cellKey: string;
  value: string | number | undefined | null | boolean | ReactNode;
  colSpan?: number;
  editable?: boolean;
  textAlign?: "left" | "right" | "center";
  verticalAlign?: "top" | "bottom" | "middle" | "inherit";
  onChangeCellValue?: ({
    value,
    key,
  }: {
    value: string | number | undefined | null | boolean;
    key: string;
  }) => void;
  dataTestid?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
  isCurrentlyActive?: boolean;
  manageCellActiveStatus?: ({
    activate,
    row,
    col,
  }: {
    activate: boolean;
    row?: number;
    col?: number;
  }) => void;
}

const Cell: React.FC<ICell> = ({
  cellKey: key,
  value,
  editable,
  colSpan,
  textAlign = "left",
  verticalAlign = "inherit",
  onChangeCellValue,
  dataTestid,
  onKeyDown,
  isCurrentlyActive,
  manageCellActiveStatus,
}) => {
  const handleClick = useCallback(() => {
    if (editable) {
      manageCellActiveStatus &&
        manageCellActiveStatus({
          activate: true,
        });
    }
  }, [editable, manageCellActiveStatus]);

  const handleSave = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeCellValue &&
        onChangeCellValue({ value: event.target.value, key });
    },
    [onChangeCellValue, key]
  );

  const onTabClick = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (event.key === "Tab") {
        event.preventDefault();
        onKeyDown && onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const styles = useMemo(
    () => ({
      border: `1px double ${colors.blue40}`,
      boxShadow: isCurrentlyActive
        ? `inset 0 0 0 2px ${colors.blue100}`
        : "none",
      padding: 2,
      verticalAlign,
      "@media print": { color: colors.black },
    }),
    [isCurrentlyActive, verticalAlign]
  );

  return (
    <TableCell
      key={key}
      colSpan={colSpan}
      sx={styles}
      align={textAlign}
      onClick={handleClick}
      onKeyDown={onTabClick}
      data-testid="table-column-cell"
      data-test-id={`${key.toLowerCase().replaceAll(" ", "-")}-cell`}
    >
      {!isCurrentlyActive && (
        <Text whiteSpace="pre-line" variant="text2" data-testid="cell-value">
          {value}
        </Text>
      )}
      {editable && isCurrentlyActive && (
        <ClickAwayListener
          onClickAway={() =>
            manageCellActiveStatus &&
            manageCellActiveStatus({ activate: false })
          }
        >
          <TextField
            autoFocus
            defaultValue={value}
            onChange={handleSave}
            data-testid="edit-data-cell-value-field"
            multiline={true}
            sx={{
              width: "100%",
              minWidth: "200px",
              // These styles are effecting the base MUI styles for the TextField
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-inputMultiline": {
                padding: 0,
              },
              "& input": {
                padding: 0,
              },
            }}
          />
        </ClickAwayListener>
      )}
    </TableCell>
  );
};

export default Cell;
